/*
* === src/pages/public/BillingPolicyPage.js ===
*/
import React, { useEffect } from 'react';
import { Container, Typography, Paper, Box, Divider } from '@mui/material';
import { Helmet } from 'react-helmet-async';

const BillingPolicyPage = () => {
    // Scroll to top on load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Helmet>
                <title>Subscription & Refund Policy | BrightSparc</title>
                <meta name="description" content="BrightSparc Subscription, Cancellation, and Refund Policies." />
            </Helmet>

            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    
                    <Typography variant="h3" fontWeight="800" gutterBottom>
                        Subscription & Refund Policy
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                        Last Updated: May 2026
                    </Typography>

                    <Box sx={{ mb: 4, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                        <Typography variant="body1">
                            Welcome to BrightSparc. These policies govern the billing, cancellation, and refund processes for all paid subscription tiers offered on the BrightSparc platform. By subscribing to a paid plan, you agree to the following terms.
                        </Typography>
                    </Box>

                    {/* 1. Subscription & Payment Terms */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>1. Subscription & Payment Terms</Typography>
                        <Typography variant="body1" paragraph>
                            BrightSparc operates on an auto-renewing subscription model to ensure uninterrupted access to your funder dashboard and applicant data.
                        </Typography>
                        <Typography component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                            <li><strong>Billing Cycles:</strong> Subscriptions are billed in advance on a recurring and periodic basis (monthly or annually).</li>
                            <li><strong>Auto-Renewal:</strong> Your subscription will automatically renew at the end of each billing cycle under the same conditions unless canceled.</li>
                            <li><strong>Payment Methods:</strong> We accept major credit and debit cards via our secure payment gateway. By submitting payment information, you authorize us to charge all subscription fees to your payment instrument.</li>
                            <li><strong>Failed Payments:</strong> If automatic billing fails, we will notify you and provide a 7-day grace period before downgrading or suspending account features.</li>
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* 2. Cancellation Policy */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>2. Cancellation Policy</Typography>
                        <Typography variant="body1" paragraph>
                            You have the right to cancel your BrightSparc subscription at any time with no hidden cancellation fees.
                        </Typography>
                        <Typography component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                            <li><strong>How to Cancel:</strong> Navigate to <em>System Settings &gt; Billing & Plan &gt; Manage Subscription</em> inside your SuperUser portal and follow the prompts.</li>
                            <li><strong>Effect of Cancellation:</strong> Canceling stops all future recurring charges. Your account will remain active at your current tier until the end of your current paid billing cycle.</li>
                            <li><strong>Data Retention:</strong> After your billing cycle ends, your account will be downgraded. We retain your historical records for 90 days before permanent deletion.</li>
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* 3. Refund Policy */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>3. Refund Policy</Typography>
                        <Typography variant="body1" paragraph>
                            Because BrightSparc provides immediate, digital access to our software, our refund policy adheres to standard B2B SaaS industry practices.
                        </Typography>
                        <Typography component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                            <li><strong>Standard Rule:</strong> All subscription charges are <strong>non-refundable</strong>. We do not provide refunds or credits for partial months, unused time, or mid-cycle downgrades.</li>
                            <li><strong>Annual Subscriptions:</strong> You may request a refund within the first 7 days of your initial purchase if the platform does not meet your requirements. Beyond this window, they are non-refundable.</li>
                            <li><strong>Accidental Auto-Renewals:</strong> We cannot process a refund for a renewed cycle once the payment has cleared. Please cancel prior to your renewal date.</li>
                            <li><strong>Exceptions:</strong> In cases of duplicate billing errors, email <em>support@brightsparc.co.za</em> for a manual review.</li>
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* 4. Upgrades and Downgrades */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>4. Upgrades and Downgrades</Typography>
                        <Typography component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                            <li><strong>Upgrading:</strong> Mid-cycle upgrades are billed immediately for the prorated difference for the remainder of the cycle.</li>
                            <li><strong>Downgrading:</strong> Downgrades take effect at the start of your <em>next</em> billing cycle. We do not issue prorated refunds for downgrades.</li>
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box>
                        <Typography variant="body2" color="text.secondary" align="center">
                            If you have any questions regarding your billing, please contact our support team at <strong>support@brightsparc.co.za</strong>.
                        </Typography>
                    </Box>

                </Paper>
            </Container>
        </>
    );
};

export default BillingPolicyPage;